import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ElementRef,
  HostBinding,
  Injector,
  Input,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { VirtualScrollerComponent } from '@iharbeck/ngx-virtual-scroller';
import { fromEvent } from 'rxjs';
import { VirtualScrollContextService } from '../../services';
import { BaseRendererV2 } from '../base-renderer-v2/base-renderer-v2.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export const VIRTUAL_SCROLL_BUFFER = 10;

/**
 * The best for rendering expandable/collapsable items.
 * Use gapBetweenItems input and avoid gap CSS property from being used as it affects item size calculation and this could
 * make scrolling not smooth/twitchy.
 * Use it like the following:
 * <app-virtual-scroll-renderer-v2
 *  [items]="someItems"
 *  [idSelector]="selectId"
 *  [gapBetweenItems]="4"
 * >
 *  <ng-template #itemTemplate let-item="item">
 *    <div>{{item}}</div>
 *  </ng-template>
 * </app-virtual-scroll-renderer-v2>
 */
@Component({
  selector: 'app-virtual-scroll-renderer-v2',
  templateUrl: './virtual-scroll-renderer-v2.component.html',
  providers: [VirtualScrollContextService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class VirtualScrollRendererV2Component extends BaseRendererV2 implements AfterViewInit {
  @HostBinding('class')
  private readonly classes = 'block w-full h-full';

  @ViewChild('scroller', { static: true })
  private virtualScroller: VirtualScrollerComponent;

  @ViewChild('scrollable', { static: true, read: ElementRef })
  private scrollableElement: ElementRef<HTMLElement>;

  @ContentChild('itemTemplate', { static: true })
  protected itemTemplate: TemplateRef<any>;

  protected itemIds: string[] = [];

  @Input()
  virtualScrollBuffer = VIRTUAL_SCROLL_BUFFER;

  @Input()
  scrollOffset: number;

  @Input()
  throttleTime = 0;

  @Input()
  parentScroll: Element;

  /** Number corresponding to tailwind sizes */
  @Input()
  gapBetweenItems: '1' | '2' | '3' | '4' | '5' | '6' | '7';

  get hasScrollbar(): boolean {
    return this.virtualScroller?.viewPortInfo.maxScrollPosition > 0;
  }

  protected get gapClass(): string {
    return this.gapBetweenItems ? `pb-${this.gapBetweenItems}` : undefined;
  }

  constructor(injector: Injector, private virtualScrollContext: VirtualScrollContextService) {
    super(injector);
  }

  ngAfterViewInit(): void {
    fromEvent(this.scrollableElement.nativeElement, 'scroll')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.scrolled.emit());

    this.virtualScrollContext.init({
      throttleTimeMs: this.throttleTime,
    });

    this.scrolled
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.virtualScrollContext.onScroll());
  }

  protected async scrollToIdBaseAsync(itemId: string, scrollToCenter = false): Promise<void> {
    const index = this.itemsMap.get(itemId)?.index;

    if (index >= 0) {
      await new Promise((resolve) => {
        if (scrollToCenter) {
          const scrollContainerHeight = this.elementRef.nativeElement.offsetHeight;

          this.virtualScroller.scrollToIndex(index, undefined, -(scrollContainerHeight / 2), undefined, () => resolve(undefined));
        } else {
          this.virtualScroller.scrollToIndex(index, undefined, this.scrollOffset ?? undefined, undefined, () => resolve(undefined));
        }
      });
    }
  }
}
